import { useEffect } from "react";
import { useSelector } from "react-redux";
import { isRealLoanGuid } from "../../utils/normalize-xml-for-create-loan";
import { useSaveCurrentLoanProgressMutation } from "./currentLoanApiSlice";
import {
	selectLoanGuid,
	selectCurrentStage,
	selectSelectedPath,
	selectSelectedProgram,
	selectLockDecision,
	selectAuraResult,
	selectFicoScore,
	selectExceptionStatus,
	selectSectionStatuses,
	selectVisitedStages,
	selectIncomeDocumentationType,
	selectConditions,
	selectProgressHydrated,
	selectProgressData,
} from "./currentLoanSlice";

// Keeps the backend's ClearPathSession row in sync with the AURA-level qualify state
// in Redux, so a loan resumed later (e.g. from Upload History) can rehydrate it via
// getCurrentLoanProgress. Fire-and-forget background autosave — same "silent on
// failure" precedent already used by Dashboard's own autosaveSection.
export const useSyncAuraProgress = () => {
	const loanGuid = useSelector(selectLoanGuid);
	const currentStage = useSelector(selectCurrentStage);
	const selectedPath = useSelector(selectSelectedPath);
	const selectedProgram = useSelector(selectSelectedProgram);
	const lockDecision = useSelector(selectLockDecision);
	const auraResult = useSelector(selectAuraResult);
	const ficoScore = useSelector(selectFicoScore);
	const exceptionStatus = useSelector(selectExceptionStatus);
	const sectionStatuses = useSelector(selectSectionStatuses);
	const visitedStages = useSelector(selectVisitedStages);
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const conditions = useSelector(selectConditions);
	const progressHydrated = useSelector(selectProgressHydrated);
	const progressData = useSelector(selectProgressData);

	const [saveCurrentLoanProgress] = useSaveCurrentLoanProgressMutation();

	// Deliberately does NOT skip the first run after progressHydrated flips true:
	// this hook is mounted on 5 different pages, and a page transition (dispatch
	// immediately followed by navigate, e.g. path-select's handleBegin) can unmount
	// the origin page before its own effect re-fires with the new value — the
	// destination page's first run is what actually persists it. The cost is one
	// redundant PATCH right after hydration that writes back the same values just
	// read (harmless/idempotent) — far safer than silently dropping a real change.
	//
	// Gating on progressHydrated (rather than firing immediately on mount) matters
	// because resumeLoan resets AURA state to blanks before Dashboard's
	// getCurrentLoanProgress GET has a chance to restore the real saved values —
	// without this gate, this hook could PATCH those blanks to the backend and lose
	// the real progress in a race against the GET-then-hydrate round trip.
	useEffect(() => {
		if (!isRealLoanGuid(loanGuid) || !progressHydrated) return;
		saveCurrentLoanProgress({
			loanGuid,
			current_stage: currentStage,
			selected_path: selectedPath,
			selected_program: selectedProgram,
			lock_decision: lockDecision,
			aura_result: auraResult,
			fico_score: ficoScore,
			exception_status: exceptionStatus,
			section_statuses: sectionStatuses,
			visited_stages: visitedStages,
			income_documentation_type: incomeDocumentationType,
			conditions,
			progress_data: progressData,
		})
			.unwrap()
			.catch(() => {
				// silent — this is a background autosave, not a user-initiated save
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		loanGuid,
		progressHydrated,
		currentStage,
		selectedPath,
		selectedProgram,
		lockDecision,
		auraResult,
		ficoScore,
		exceptionStatus,
		sectionStatuses,
		visitedStages,
		incomeDocumentationType,
		conditions,
		progressData,
	]);
};
